import { call, put, takeLatest } from 'redux-saga/effects';
import { select } from 'redux-saga/effects';
import {
  submitDocSuccess,
  submitDocFailed,
  submitRefiDocsSuccess,
  submitRefiDocsFailed,
  feesError,
  downloadAllSuccess,
  downloadAllFailed,
} from './action';
import { Api } from './api';
import { SUBMIT_DOC, SUBMIT_REFIDOC, SAVE_DOCS_FORM_DATA, SAVE_REFIDOCS_FORM_DATA, DOWNLOAD_ALL, GET_LIEN_HOLDER_INFO } from './types';
import { GET_LIEN_HOLDER_INFO_FAILED, GET_LIEN_HOLDER_INFO_SUCCESS } from '../types';
import { STATE_MAPPER } from '../../components/docs/constants';

// Keys the backend always echoes regardless of whether any documents were
// returned. A "real" success has at least one additional array-valued entry
// (each entry is `[url, description]`). If only metadata keys come back the
// upstream ATC call effectively returned nothing -- treat that as a failure
// so the user sees an error instead of an empty output page that requires a
// resubmit to populate.
const DOCS_METADATA_KEYS = ['Inputs', 'TransactionID', 'Type', 'Notes', 'Error'];

const hasUsableDocsPayload = (data) => {
  if (!data || typeof data !== 'object') return false;
  return Object.entries(data).some(
    ([key, value]) => !DOCS_METADATA_KEYS.includes(key) && Array.isArray(value)
  );
};

const extractDocsErrorMessage = (data) => {
  if (data && typeof data === 'object' && data.Error) {
    return data.Error;
  }
  return 'No documents were returned. Please try again.';
};

function* submitDoc(action) {
  const state = yield select();
  const Error = state.fees.feesError;
  try {
    const { locationInfo, vehicleInfo, transactionInfo, firstBuyerInfo, secondBuyerInfo, firstSellerInfo, secondSellerInfo, insuranceInfo, lienHolderInfo } = state.forms;

    const combinedPayload = {
      ...locationInfo,
      ...vehicleInfo,
      ...transactionInfo,
      ...firstBuyerInfo,
      ...secondBuyerInfo,
      ...firstSellerInfo,
      ...secondSellerInfo,
      ...insuranceInfo,
      ...lienHolderInfo,
    };

    yield put({
      type: SAVE_DOCS_FORM_DATA,
      payload: combinedPayload,
    });

    const response = yield call(Api.submitDoc, combinedPayload);
    if (response?.status === 200) {
      // The backend can return HTTP 200 with an empty payload (or an explicit
      // `Error` field) when the upstream ATC service is flaky. Without this
      // check we'd dispatch success and navigate to an output page that has
      // nothing to show -- the same data on resubmit would render correctly
      // because the upstream call usually succeeds the second time.
      if (!hasUsableDocsPayload(response.data)) {
        if (Error) {
          yield put(feesError(Error));
        }
        yield put(submitDocFailed(extractDocsErrorMessage(response.data)));
        return;
      }

      yield put(submitDocSuccess(response.data));
      if (Error) {
        yield put(feesError(Error));
        return;
      }
      if (action.navigate && action.path) {
        action.navigate(action.path);
      }
      else {
        action.navigate('docsOutput');
      }
    } else {
      if (Error) {
        yield put(feesError(Error));
      }
      yield put(submitDocFailed(response?.data?.message || response?.data?.error  || 'Failed to submit docs'));
    }
  } catch (error) {
    if (Error) {
      yield put(feesError(Error));
    }
    yield put(submitDocFailed(error.message || 'Unexpected error Occurred in docs'));
  }
}

function* submitRefiDoc(action) {
  const state = yield select();
  const Error = state.fees.feesError;
  try {
    const { locationInfo, vehicleInfo, transactionInfo, firstOwnerInfo, secondOwnerInfo, lienHolderInfo } = state.forms;

    const combinedPayload = {
      ...locationInfo,
      ...vehicleInfo,
      ...transactionInfo,
      ...firstOwnerInfo,
      ...secondOwnerInfo,
      ...lienHolderInfo,
    };

    yield put({
      type: SAVE_REFIDOCS_FORM_DATA,
      payload: combinedPayload,
    });

    const response = yield call(Api.submitRefiDoc, combinedPayload);
    if (response?.status === 200) {
      // Same defensive check as submitDoc: a 200 with no document arrays
      // means the upstream returned nothing usable, so surface an error
      // instead of navigating to an empty output page.
      if (!hasUsableDocsPayload(response.data)) {
        if (Error) {
          yield put(feesError(Error));
        }
        yield put(submitRefiDocsFailed(extractDocsErrorMessage(response.data)));
        return;
      }

      yield put(submitRefiDocsSuccess(response.data));
      if (Error) {
        yield put(feesError(Error));
        return;
      }
      if (action.navigate && action.path) {
        action.navigate(action.path);
      }
      else {
        action.navigate('docsOutput');
      }
    } else {
      if (Error) {
        yield put(feesError(Error));
      }
      yield put(submitRefiDocsFailed(response?.data?.message || 'Failed to submit refi docs'));
    }
  } catch (error) {
    if (Error) {
      yield put(feesError(Error));
    }
    yield put(submitRefiDocsFailed(error.message || 'Unexpected error Occurred in refi docs'));
  }
}

function* downloadAll(action) {
  const state = yield select();
  const docsResponse = state.docs.docsResponse;

  const links = document.querySelectorAll('.docs-output-form a');
  let urls = Array.from(links).map(link => link.href);

  // Define valid document extensions
  const validExtensions = ['.pdf', '.docx', '.xlsx', '.txt', '.csv', '.jpg', '.png', '.pptx', '.rtf'];

  // Filter URLs to keep only document URLs (PDF, DOCX, etc.)
  urls = urls.filter(url => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
        // Check if the URL ends with a valid document extension
        const fileExtension = url.substring(url.lastIndexOf('.')).toLowerCase();
        return validExtensions.includes(fileExtension);
    }
    return true;
  });

  const file_urls = urls;
  const pdfBlob = action.payload;
  const transaction_id = docsResponse?.TransactionID || ''

  const formData = new FormData();
  // formData.append("pdfBlob", pdfBlob);
  formData.append("file_urls", JSON.stringify(file_urls));
  formData.append("transaction_id", transaction_id);

  try {
    const response = yield call(Api.downloadAll, formData, pdfBlob);

    if (response?.status === 200) {
      yield put(downloadAllSuccess());
    } else {
      yield put(downloadAllFailed());
    }
  } catch (error) {
    yield put(downloadAllFailed());
  }
}

function* getLienHolderInfo(){
  const state = yield select();
  const State = state.forms.locationInfo.state;

  const RStateMapperAry = Object
    .entries(STATE_MAPPER)
    .map(([key, value]) => [value, key]);

  const RStateMapper = Object.fromEntries(RStateMapperAry)

  const payload = {
    state: RStateMapper[State],
    username: localStorage.getItem('email'),
  }

  try {
    const response = yield call(Api.getLienHolderInfo, payload);

    if (response?.status === 200) {
      yield put({
        type: GET_LIEN_HOLDER_INFO_SUCCESS,
        payload: response.data,
      });
    } else {
      yield put({
        type: GET_LIEN_HOLDER_INFO_FAILED,
        payload: response.data,
      });
    }
  } catch (error) {
    yield put({
      type: GET_LIEN_HOLDER_INFO_FAILED,
      payload: "Unexpected Error",
    });
  }
}


function* docsSaga() {
  yield takeLatest(SUBMIT_DOC, submitDoc);
  yield takeLatest(SUBMIT_REFIDOC, submitRefiDoc);
  yield takeLatest(DOWNLOAD_ALL, downloadAll);
  yield takeLatest(GET_LIEN_HOLDER_INFO, getLienHolderInfo);
}

export default docsSaga;
